import { createWireStore, RouterState } from '@oneappexchange/lwc-wired';
import { createRouterStore } from '@oneappexchange/lwc-wired-router';
import { InitI18nLinksOptions, initializeI18nLinks } from 'i18n/link';
import { LightningElement } from 'lwc';
import { NavigateOptions } from 'navigo';
import { initializePageLinks } from 'page/link';
import { IS_DEVELOPMENT, PUBLIC_URL } from '~/modules/config';
import { FeatureFlags } from '~/modules/feature_flags';
import { getUserLanguage } from '~/modules/helpers/languageHelper';
import {
    EXPLORE_BASE_ROUTE,
    LEARN_BASE_ROUTE,
    ORDERS_BASE_ROUTE,
    ORDERS_PARAMS,
    Routes,
    TRIALS_BASE_ROUTE,
} from '~/modules/routes';
import { scrollToTop } from '~/modules/utilities/browser';
import { ENGLISH_LANGUAGE_CODE } from '~/modules/utilities/language_utils';
import { extractURLComponents } from '~/modules/utilities/route_utils';
import { isAgentExchange } from '~/modules/utilities/router';
import { logger } from '../utilities/logger';

// In the future we might put Routes.CONSULTANT_LISTING in this array
export const LISTING_DETAIL_ROUTES: string[] = [Routes.LISTING];

type ComponentRouteMap = Record<
    string,
    () => Promise<{
        default: typeof LightningElement;
    }>
>;

// Add additional routes here
const routes: ComponentRouteMap = {
    '': () => import('pages/home'),
    [Routes.HOME]: () => import('pages/home'),
    [Routes.SEARCH]: () => import('pages/search'),
    [Routes.EXPLORE]: () => import('pages/explore'),
    [Routes.LEARN_HOME]: () => import('pages/learnHome'),
    [Routes.LEARN]: () => import('pages/learn'),
    [Routes.COLLECTION]: () => import('pages/collections'),
    [Routes.COLLECTIONS_HOME]: () => import('pages/collections'),
    [Routes.CALLBACK]: () => import('pages/callback'),
    [Routes.LOGIN]: () => import('pages/login'),
    [Routes.SIGN_UP]: () => import('pages/signup'),
    ...LISTING_DETAIL_ROUTES.reduce((mergeRoutes: ComponentRouteMap, route) => {
        mergeRoutes[route] = () => import('pages/listingDetail');
        return mergeRoutes;
    }, {}),
    [Routes.FAVORITE_LISTINGS]: () => import('pages/favoritesPage'),
    [Routes.ERROR]: () => import('pages/error'),
    [Routes.NOT_FOUND]: () => import('pages/error'),
    [Routes.TRIALS]: () => import('pages/try'),
    [Routes.TRIALS_CONFIRMATION]: () => import('pages/try'),
};

if (FeatureFlags.FF_CUSTOMER_CONSOLE && getUserLanguage() === ENGLISH_LANGUAGE_CODE) {
    routes[Routes.ORDERS] = () => import('pages/orders');
}

const exported = createRouterStore({
    routes,
    storeName: 'routerStore',
    developmentMode: IS_DEVELOPMENT,
    routeLoadDurationCallback: (_, duration) => {
        logger.metric(
            'ept',
            window.location.href.replace('https://', '').replace('http://', '').replace(window.location.host, ''),
            duration,
        );
    },
});

export const routerStore = exported.routerStore;

export const testIsNavigoLink = exported.testIsNavigoLink;

// Do server side navigation on the home page and client side navigation on the listing detail, search and explore pages
// TODO: This can be removed now that the homepage is in Japanese
export function navigate(to: string, options?: NavigateOptions | undefined) {
    if (isAgentExchange()) {
        // We have to navigate outside of the agentexchange domain
        const url = new URL(to, PUBLIC_URL);
        return window.location.assign(url.href);
    }

    const pathName = extractURLComponents(to)?.pathName || '';
    if (
        pathName === Routes.LISTING ||
        pathName === Routes.SEARCH ||
        pathName === Routes.ERROR ||
        pathName === Routes.LOGIN ||
        pathName === Routes.SIGN_UP ||
        pathName === Routes.CALLBACK ||
        pathName.startsWith(TRIALS_BASE_ROUTE) ||
        pathName.startsWith(EXPLORE_BASE_ROUTE) ||
        pathName.startsWith(ORDERS_BASE_ROUTE) ||
        pathName.startsWith(LEARN_BASE_ROUTE)
    ) {
        return exported.navigate(to, options);
    }
    return window.location.assign(to);
}

// Scroll to top and update page title & description when URL or listingId changes
routerStore.subscribe(scrollToTop, selectUrl);
routerStore.subscribe(scrollToTop, selectListingIdQueryParam);

// Connect page-link instances to our navigate function
initializePageLinks(navigate, testIsNavigoLink);
// enable custom link parsing to events
const i18nOptions: InitI18nLinksOptions = {
    useCustomEvents: true,
};
initializeI18nLinks(navigate, testIsNavigoLink, i18nOptions);

export const WireRouter = createWireStore(routerStore);

export function selectUrl(state: RouterState = routerStore.getState()) {
    return state.url;
}

export function selectQueryString(state: RouterState = routerStore.getState()) {
    return state.queryString;
}

export function selectQueryParams(state: RouterState) {
    return state.params || {};
}

export function selectQueryParam(state: RouterState, param: string, defaultValue?: string): string | undefined {
    const queryParams = selectQueryParams(state);
    const result = queryParams[param];
    return result || defaultValue;
}

export function selectListingIdQueryParam(state: RouterState): string | undefined {
    return selectQueryParam(state, 'listingId');
}

export function selectPreviewIdQueryParam(state: RouterState): string | undefined {
    return selectQueryParam(state, 'axwp-preview-id');
}

export function selectOrgIdQueryParam(state: RouterState) {
    return selectQueryParam(state, ORDERS_PARAMS.ORG_ID);
}

export function selectScrollToQueryParam(state: RouterState): string | undefined {
    return selectQueryParam(state, 'scrollTo');
}

export function selectMatch(state: RouterState) {
    const { url, queryString, hashString, data, params } = state;
    return { url, queryString, hashString, data, params };
}
